import url from 'url';
import classNames from 'clsx';
import PropTypes from 'prop-types';
import { useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import viewTypes from 'src/common/constants/auth/viewTypes';
import { verifyDevicePin } from '../../actions/auth';
import { pairAuthFlows } from '../../constants/auth/oauth';
import keyCodes from '../../constants/keyCodes';
import { pinCodePageLocalizationMap } from '../../constants/localizations/pinCode';
import { AUTHORIZE_PATH } from '../../constants/paths';
import { PAIR_AUTH_FLOW } from '../../constants/queryParams';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import { selectPinCodeVerification } from '../../selectors/auth';
import clientRedirect from '../../utils/clientRedirect';
import AuthFlowLayout from '../auth/shared/AuthFlowLayout';
import PremiumGoldButton from '../shared/button/PremiumGoldButton';
import css from './pin-code.module.scss';

export const PIN_CODE_LENGTH = 6;

// exported for testing
export const PinCode = ({
  isPinValidationError,
  devicePartnerId,
  deviceSerial,
  sku,
  actions,
  location,
}) => {
  const { getLocalizedText } = useContext(LocationAndLocalizationContext);
  const [values, setValues] = useState(() => ({
    ...Array(PIN_CODE_LENGTH).fill(''),
  }));
  const [isError, setIsError] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  // submissionAttemptComplete was added so the component can respond to multiple failed pin attempts
  const [submissionAttemptComplete, setSubmissionAttemptComplete] =
    useState(false);
  const { titleKey, errorKey, buttonLabelKey } =
    pinCodePageLocalizationMap[location?.pathname] || {};

  /* eslint-disable no-param-reassign */
  const createPinString = (pinObj) =>
    Object.keys(pinObj).reduce((pin, key) => (pin += pinObj[key]), '');
  /* eslint-enable no-param-reassign */

  useEffect(() => {
    if (isPinValidationError && submissionAttemptComplete) {
      setIsError(true);
      setSubmissionAttemptComplete(false);
    }
  }, [isPinValidationError, submissionAttemptComplete]);

  useEffect(() => {
    if (devicePartnerId && deviceSerial) {
      setSubmissionAttemptComplete(false);
      const pathWithQuery = url.format({
        pathname: AUTHORIZE_PATH,
        query: {
          [PAIR_AUTH_FLOW]: pairAuthFlows.pin,
          serial: deviceSerial,
          partnerid: devicePartnerId,
          usercode: createPinString(values),
          vt: viewTypes.signUp,
          sku,
        },
      });

      clientRedirect(pathWithQuery);
    }
  }, [devicePartnerId, deviceSerial, sku, values]);

  const updateValues = (index, value) => {
    setValues((state) => ({
      ...state,
      [index]: value,
    }));
  };

  useEffect(() => {
    if (Object.keys(values).every((k) => !!values[k])) {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  }, [values]);

  const focusOnNextSibling = (e) => {
    const nextSibling = e?.target?.nextElementSibling;

    if (
      nextSibling?.nodeName?.toLowerCase() === 'input' &&
      nextSibling?.value === ''
    ) {
      nextSibling.focus();
    }
  };

  const handleChange = (e, index) => {
    const value = e?.target?.value || '';
    const pinRegex = /[a-zA-Z0-9]/;
    if (isError) {
      setIsError(false);
    }

    if (!pinRegex.test(value)) {
      return;
    }

    updateValues(index, value.toUpperCase());
    focusOnNextSibling(e);
  };

  const handleKeyDown = (e, index) => {
    const key = e?.keyCode || e?.charCode;
    const isDelete = key === keyCodes.BACKSPACE || key === keyCodes.DELETE;

    if (isDelete && values[index] === '') {
      e?.target?.previousSibling?.focus();
    } else if (isDelete && values[index]) {
      updateValues(index, '');
    } else if (values[index]) {
      focusOnNextSibling(e);
    }
  };

  const handleFocus = (e) => {
    const prevSibling = e?.target?.previousSibling;
    if (prevSibling?.value === '') {
      prevSibling.focus();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsDisabled(true);
    actions.verifyDevicePin(createPinString(values));

    setSubmissionAttemptComplete(true);
  };

  const getInput = (index) => (
    <input
      type="text"
      maxLength={1}
      autoFocus={Number.parseInt(index, 10) === 0}
      key={`input-${index}`}
      className={css.input}
      value={values[index]}
      data-testid={`input-${index}`}
      onChange={(e) => handleChange(e, index)}
      onFocus={handleFocus}
      onKeyDown={(e) => handleKeyDown(e, index)}
    />
  );

  return (
    <AuthFlowLayout
      logoContainerClassName={css.logoContainer}
      useFullTuneInLogo
      hideFooter
      hideHeaderText
    >
      <div className={css.contentContainer} data-testid="pinCodePageContent">
        <div className={css.pinPageTitle} data-testid="pinCodePageTitle">
          {getLocalizedText(titleKey)}
        </div>
        <form onSubmit={handleSubmit} className={css.form}>
          <div
            className={css.inputFieldsContainer}
            data-testid="pinCodePageInputsContainer"
          >
            {Object.keys(values).map((v, i) => getInput(i))}
          </div>
          {isError && (
            <div className={css.errorText} data-testid="pinCodePageErrorText">
              {getLocalizedText(errorKey)}
            </div>
          )}
          <PremiumGoldButton
            id="pinCodeSubmitButton"
            dataTestId="pinCodeSubmitButton"
            label={getLocalizedText(buttonLabelKey)}
            className={classNames(css.submitButton, {
              [css.withErrorText]: isError,
            })}
            isDisabled={isDisabled}
            onClick={handleSubmit}
            type="submit"
          />
        </form>
      </div>
    </AuthFlowLayout>
  );
};

PinCode.propTypes = {
  isPinValidationError: PropTypes.bool.isRequired,
  actions: PropTypes.object.isRequired,
  devicePartnerId: PropTypes.string,
  deviceSerial: PropTypes.string,
  sku: PropTypes.string,
  location: PropTypes.object.isRequired,
};

export function mapStateToProps(state) {
  const { hasFailed, partnerId, serial, sku } =
    selectPinCodeVerification(state);
  return {
    isPinValidationError: hasFailed || false,
    devicePartnerId: partnerId,
    deviceSerial: serial,
    sku,
  };
}

export function mapDispatchToProps(dispatch) {
  return { actions: bindActionCreators({ verifyDevicePin }, dispatch) };
}

export default connect(mapStateToProps, mapDispatchToProps)(PinCode);
